import { useEffect, useState } from 'react'
import { useLiveQuery } from '@tanstack/react-db'

import {
  graceRemainingMs,
  isCancellable,
  recentlySentPhones,
} from '@/db/application/whatsappMessages'
import { toZaDateKey, toZaTime } from '@/lib/datetime'
import { useDb } from '@/lib/dbContext'
import {
  WhatsAppScreen,
  type WhatsAppMessageRow,
} from '@/screens/WhatsAppScreen'

/** The message queue + log — the WhatsApp page's "Messages" tab. */
export function WhatsAppMessagesTab() {
  const db = useDb()
  const result = useLiveQuery((q) =>
    q.from({ messages: db.whatsappMessagesCollection }),
  )
  const customersResult = useLiveQuery((q) =>
    q.from({ customers: db.customersCollection }),
  )
  const settingsResult = useLiveQuery((q) =>
    q.from({ settings: db.appSettingsCollection }),
  )

  useEffect(() => {
    void db.appSettingsCollection.preload?.()
  }, [db])

  const sendingEnabled =
    settingsResult.data?.[0]?.whatsappSendingEnabled ?? false
  const messages = result.data ?? []
  const hasPending = messages.some((message) => message.status === 'Queued')
  const [now, setNow] = useState(() => new Date().toISOString())

  // Tick once a second while messages are counting down their grace period,
  // so the "Sends in Ns" badges and cancel buttons stay honest.
  useEffect(() => {
    if (!hasPending) return
    const interval = setInterval(
      () => setNow(new Date().toISOString()),
      1000,
    )
    return () => clearInterval(interval)
  }, [hasPending])

  if (result.isLoading) {
    return <p className="text-sm text-slate-600">Loading messages…</p>
  }

  if (result.isError) {
    return (
      <p className="text-sm text-slate-600">
        Unable to load messages right now.
      </p>
    )
  }

  const customerFor = (customerId?: string) =>
    customerId
      ? customersResult.data?.find((customer) => customer.id === customerId)
      : undefined

  // Recipients that got a successful send in the last 24h — their resends are
  // held back so the same template isn't fired twice.
  const recentlySent = recentlySentPhones(messages, now)

  const rows: WhatsAppMessageRow[] = messages.map((message) => {
    const customer = customerFor(message.customerId)
    return {
      id: message.id,
      toPhone: message.toPhone,
      customerName: customer?.fullName,
      customerId: message.customerId,
      customerPublicId: customer?.publicId,
      serviceId: message.serviceId,
      servicePublicId: message.servicePublicId,
      content: message.content,
      status: message.status,
      failureReason: message.failureReason,
      sentAtLabel: message.sentAt
        ? `${toZaDateKey(message.sentAt)} ${toZaTime(message.sentAt)}`
        : undefined,
      sortTimestamp: message.sentAt ?? message.updatedAt,
      graceSecondsRemaining: Math.ceil(graceRemainingMs(message, now) / 1000),
      cancellable: isCancellable(message, now),
      recentlySent: recentlySent.has(message.toPhone),
    }
  })

  return (
    <WhatsAppScreen
      rows={rows}
      sendingEnabled={sendingEnabled}
      onCancelMessage={(id) => db.cancelWhatsAppMessage(id)}
      onResendMessage={(id) => db.resendWhatsAppMessage(id)}
    />
  )
}
